import React, { useState, useEffect } from 'react';
import { Box, Switch } from '@mui/material';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import styles from '../../styles/component.module.scss';
import integrationAppStyles from './style.module.scss';
import PageHeader from '../Header/PageHeader';
import AppCard from '../AppCard';
import CustomButton from '../Button/customButton/CustomButton';
import { fetchAppList, getAuthorizeUrl, revokeAppConnection } from '../../utils/api/app_apis';
import { STATUS } from '../../utils/api/sharedfunction';
import { useSnackbar } from '../../utils/useSnackbar';
import { setActivePage } from '../../features/navigation/navigationSlice';
import DEFAULT_ROUTES from '../../constants/routes';

const UserIntegrationApp = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const activePage = useSelector((state) => state.navigation.activePage);
  const selectedProject = useSelector((state) => state.projects.selectedProject);
  const { showSnackbar } = useSnackbar();

  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(false);
  const [togglingAppId, setTogglingAppId] = useState(null);

  useEffect(() => {
    dispatch(setActivePage('App Integration'));
    fetchApps();
  }, [dispatch, selectedProject?.id]);

  const fetchApps = async () => {
    try {
      setLoading(true);
      const response = await fetchAppList({
        projectId: selectedProject?.id
      });

      if (response.status === STATUS.SUCCESS) {
        setApps(response.data || []);
      } else {
        showSnackbar(response.error || 'Failed to fetch apps', 'error');
      }
    } catch (error) {
      console.error('Failed to fetch apps:', error);
      showSnackbar('Failed to fetch apps', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleConnection = async (app, currentStatus) => {
    if (!selectedProject?.id) {
      showSnackbar('Please select a project first', 'error');
      return;
    }

    try {
      setTogglingAppId(app.id);

      if (currentStatus) {
        const response = await revokeAppConnection({
          projectId: selectedProject.id,
          appId: app.id,
        });

        if (response.status === STATUS.SUCCESS) {
          showSnackbar(response.message || 'App disconnected successfully', 'success');
          fetchApps();
        } else {
          showSnackbar(response.error || 'Failed to disconnect', 'error');
        }
      } else {
        const response = await getAuthorizeUrl({
          projectId: selectedProject.id,
          appId: app.id,
        });

        if (response.status === STATUS.SUCCESS && response.data?.authorizeUrl) {
          window.open(response.data.authorizeUrl, '_blank');
        } else {
          showSnackbar(response.error || 'Failed to get authorization URL', 'error');
        }
      }
    } catch (error) {
      console.error('Error toggling connection:', error);
      showSnackbar('Failed to toggle connection', 'error');
    } finally {
      setTogglingAppId(null);
    }
  };

  const handleBreadcrumbClick = (id) => {
    if (id === 'home') {
      dispatch(setActivePage('Projects'));
      navigate(DEFAULT_ROUTES.BASE_URL);
    }
  };

  const breadcrumbItems = [
    { label: 'Project', onClick: () => handleBreadcrumbClick('home') },
    { label: selectedProject?.name || 'Unknown Project' },
    { label: activePage }
  ];

  return (
    <Box className={styles['page-container']}>
      <PageHeader
        projectName={selectedProject?.name || null}
        title="App Integration "
        description="Connect and manage third-party integrations"
        activePage="App Integration"
        breadcrumbItems={breadcrumbItems}
      />
      <Box
        className={`${styles['cover-container-col-auto']} ${integrationAppStyles.gridContainer}`}
      >
        <Box className={integrationAppStyles.cardContainer}>
          {loading ? (
            <Box className={integrationAppStyles.loadingState}>Loading apps...</Box>
          ) : apps.length === 0 ? (
            <Box className={integrationAppStyles.emptyState}>No apps available for integration.</Box>
          ) : (
            apps.map((app) => {
              const isConnected = app.is_connected === true;
              const isToggling = togglingAppId === app.id;

              return (
                <AppCard
                  key={app.id}
                  header={app.name}
                  description={app.description || 'No description available'}
                  icon={app.image_url}
                  showButton={true}
                  toggleButton={
                    <Switch
                      checked={isConnected}
                      onChange={() => handleToggleConnection(app, isConnected)}
                      disabled={isToggling || loading || !selectedProject?.id}
                    />
                  }
                >
                  <Box className={integrationAppStyles.statusContainer}>
                    <span className={`${integrationAppStyles.statusText} ${isConnected ? integrationAppStyles.connected : integrationAppStyles.notConnected}`}>
                      {isConnected ? 'Connected' : 'Not Connected'}
                    </span>
                  </Box>
                </AppCard>
              );
            })
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default UserIntegrationApp;